import { runContextLabel, historyRowStatus } from "../../lib/operationRun.js";

export type NodeMetadata = {
  createdAt: string | null;
  updatedAt: string | null;
  ownerEmail: string | null;
};

function fmtMeta(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export function NodeMetadataFields({ meta }: { meta: NodeMetadata }) {
  return (
    <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
      <div className="field">
        <label className="label">Created</label>
        <input className="input" readOnly value={fmtMeta(meta.createdAt)} />
      </div>
      <div className="field">
        <label className="label">Owner</label>
        <input className="input" readOnly value={meta.ownerEmail ?? "—"} />
      </div>
      <div className="field">
        <label className="label">Last updated</label>
        <input className="input" readOnly value={fmtMeta(meta.updatedAt)} />
      </div>
    </div>
  );
}

export type NodeOutputRecord = {
  nodeId: string;
  nodeType: string;
  rowCount: number | null;
  durationMs: number | null;
  runAt: string | null;
  error: string | null;
  validationStatus?: "valid" | "needs_validation";
  validationError?: string | null;
  outputSchema: Array<{ name: string; type: string }> | null;
};

export type NodeRunHistoryEntry = {
  id: string;
  node_id: string;
  node_type: string;
  // Legacy (shared-provider) history uses success/failed; operation-run history
  // uses the node's run status (from_cache / succeeded / failed / skipped /
  // cancelled).
  status: string;
  // ADR-0130: the RUN's overall status + context (operation-run history only).
  // Together with `status` they distinguish an execution from an instigated
  // upstream failure ("Failed · upstream") and label the run kind.
  run_status?: string | null;
  run_context?: unknown;
  started_at: string;
  finished_at: string;
  duration_ms: number | null;
  row_count: number | null;
  // THIS node's own error. Never the run's (bugfix 2026-10-29): a step that
  // succeeded inside a run that failed elsewhere carries null here and the
  // run-level note below.
  error: string | null;
  // A labelled run-level note for a row whose step executed but whose run
  // failed anyway, e.g. "Run failed at Machine Hours".
  run_note?: string | null;
  from_cache: boolean;
  details?: Record<string, unknown> | null;
  connector_id?: string | null;
  // ADR-0128: present for operation-run-based history — opens that run's Trace.
  runId?: string | null;
};

/**
 * ADR-0130: history-row label + pill (delegates to the pure lib helper). The run
 * context + node type ride along so a persist node skipped BY DESIGN by its own
 * `execute_persist` run is not reported as an upstream failure.
 */
function historyStatusPill(entry: NodeRunHistoryEntry): { label: string; cls: string } {
  return historyRowStatus(entry.status, entry.run_status, {
    runContext: entry.run_context,
    nodeType: entry.node_type,
  });
}

export function NodeRunHistory({
  runs,
  loading,
  onSelectRun,
  onOpenTrace
}: {
  runs: NodeRunHistoryEntry[];
  loading: boolean;
  /** Kept on the prop type for back-compat; the button is gone — history auto-loads on inspector open and re-loads after Run. */
  onRefresh?: () => void;
  onSelectRun: (run: NodeRunHistoryEntry) => void;
  /** ADR-0128: open the full-chain Trace for a run-based history row. */
  onOpenTrace?: (runId: string) => void;
}) {
  if (loading) return <div className="muted">Loading run history…</div>;
  if (runs.length === 0) {
    return (
      <div className="muted">No execution history yet. Run this node to see results.</div>
    );
  }
  const anyTrace = runs.some((r) => r.runId);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontWeight: 600 }}>Run history ({runs.length})</div>
      <div style={{ maxHeight: 400, overflow: "auto" }}>
        <table className="table">
          <thead>
            <tr>
              <th>Run at</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Rows</th>
              {anyTrace ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const pill = historyStatusPill(run);
              const canTrace = Boolean(run.runId && onOpenTrace);
              return (
                <tr
                  key={run.id}
                  style={{ cursor: canTrace ? "pointer" : undefined }}
                  onClick={canTrace ? () => onOpenTrace!(run.runId!) : () => onSelectRun(run)}
                >
                  <td>
                    {new Date(run.started_at).toLocaleString()}
                    {run.run_context != null ? (
                      <div className="muted" style={{ fontSize: 11 }}>{runContextLabel(run.run_context)}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={pill.cls}>{pill.label}</span>
                    {run.error ? (
                      <div
                        title={run.error}
                        style={{ fontSize: 11, color: "var(--danger-700, #dc2626)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      >
                        {run.error}
                      </div>
                    ) : null}
                    {run.run_note ? (
                      <div className="muted" style={{ fontSize: 11 }}>{run.run_note}</div>
                    ) : null}
                  </td>
                  <td>{run.duration_ms != null ? `${run.duration_ms} ms` : "–"}</td>
                  <td>{run.row_count != null ? run.row_count.toLocaleString() : "–"}</td>
                  {anyTrace ? (
                    <td>
                      {canTrace ? (
                        <button
                          type="button"
                          className="adminListAction"
                          onClick={(e) => { e.stopPropagation(); onOpenTrace!(run.runId!); }}
                        >
                          Trace
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function NodeOutputStats({ rec }: { rec: NodeOutputRecord | undefined }) {
  if (!rec) return <div className="muted">No run metadata yet.</div>;
  return (
    <table className="table" style={{ fontSize: 13 }}>
      <tbody>
        <tr>
          <td className="muted">Last run</td>
          <td>{rec.runAt ? new Date(rec.runAt).toLocaleString() : "–"}</td>
        </tr>
        <tr>
          <td className="muted">Duration</td>
          <td>{rec.durationMs != null ? `${rec.durationMs} ms` : "–"}</td>
        </tr>
        <tr>
          <td className="muted">Rows</td>
          <td>{rec.rowCount != null ? rec.rowCount.toLocaleString() : "–"}</td>
        </tr>
        {rec.error ? (
          <tr>
            <td className="muted">Error</td>
            <td style={{ color: "var(--danger-700, #dc2626)", fontFamily: "monospace", fontSize: 12 }}>{rec.error}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}
